/**
 * Helpers de persistência/mapeamento de Contato (fonte única).
 * Usados pelo ContactsProvider e pelo painel de conversa.
 */
import { supabase } from '../../../lib/supabase';
import { Contact } from '../../../types';
import { contactPhoneKeyVariants } from '../../../utils/phoneUtils';

export const CONTACT_ROW_SELECT =
    'id, name, email, phone, whatsapp, phone_key, company, assigned_to, tags, origin_id, origins(name)';

export const ERR_NO_ORG = 'Nenhuma organização selecionada. Recarregue a página e tente novamente.';

function mapContactOrigins(c: { origins?: unknown }): { name: string } | null {
    const origins = c.origins;
    if (Array.isArray(origins) && origins.length > 0) return origins[0] as { name: string };
    return (origins as { name: string } | null) || null;
}

/** Normaliza row do Supabase (origins pode vir como objeto ou array). */
export function mapContactRow(c: Record<string, unknown> | null | undefined): Contact {
    const row = (c || {}) as Record<string, unknown>;
    return {
        ...row,
        origins: mapContactOrigins(row),
    } as Contact;
}

function mapContactLimitMessage(raw: string): string | null {
    if (!/limite de contatos/i.test(raw)) return null;
    const match = raw.match(/até\s+(\d+)\s+contatos/i);
    const limit = match?.[1];
    if (limit) {
        return `Limite de contatos atingido (máx. ${limit}). Peça ao administrador da organização para fazer upgrade do plano.`;
    }
    return 'Limite de contatos atingido. Peça ao administrador da organização para fazer upgrade do plano.';
}

export function mapContactError(err: { code?: string; message?: string; details?: string }): string {
    if (err.code === '23505') {
        const detail = `${err.message ?? ''} ${err.details ?? ''}`.toLowerCase();
        if (detail.includes('phone_key') || detail.includes('idx_contacts_org_phone')) {
            return 'Já existe um contato com este telefone nesta organização.';
        }
        return 'Já existe um contato com estes dados nesta organização.';
    }
    if (err.code === '42501') {
        if (/user_org_ids|permission denied for function auth/i.test(err.message ?? '')) {
            return 'Sua sessão expirou. Faça login novamente.';
        }
        return 'Sem permissão para salvar contatos. Verifique seu acesso à organização.';
    }
    const limitMessage = mapContactLimitMessage(err.message ?? '');
    if (limitMessage) return limitMessage;
    return err.message || 'Erro ao salvar contato. Tente novamente.';
}

/** REST/RLS sem JWT — 401 ou permission denied em auth.user_org_ids (sessão já caiu). */
export function isContactsSessionFailure(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const e = error as { code?: string; message?: string; status?: number };
    const msg = String(e.message ?? '');
    if (e.status === 401 || e.status === 403) return true;
    if (e.code === '42501' && /user_org_ids|permission denied/i.test(msg)) return true;
    if (e.code === 'PGRST301') return true;
    return /jwt expired|jwt|not authenticated|permission denied for function auth/i.test(msg);
}

export function duplicatePhoneError(name: string): Error {
    return new Error(`Já existe um contato com este telefone: ${name}.`);
}

/** Insere contato na lista local ordenada por nome (idempotente por id). */
export function upsertContactSorted(prev: Contact[], contact: Contact): Contact[] {
    if (prev.some((c) => c.id === contact.id)) return prev;
    return [...prev, contact].sort((a, b) => a.name.localeCompare(b.name));
}

export async function findDuplicateByPhoneKey(
    organizationId: string,
    phoneKey: string,
    excludeId?: string,
): Promise<{ id: string; name: string } | null> {
    const variants = contactPhoneKeyVariants(phoneKey);
    let query = supabase
        .from('contacts')
        .select('id, name')
        .eq('organization_id', organizationId)
        .in('phone_key', variants.length > 0 ? variants : [phoneKey]);

    if (excludeId) {
        query = query.neq('id', excludeId);
    }

    const { data, error } = await query.maybeSingle();
    if (error) throw error;
    return data;
}
